import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { AppBar, Box, Drawer, IconButton, List, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { hf, hfAlpha } from '../../theme';
import { NavCollapsedContext } from './navContext';
import { HFPageContainer, type HFPageContainerProps } from '../../atoms/HFPageContainer';

const NAV_COLLAPSED_KEY = 'hf_nav_collapsed';
const DRAWER_WIDTH_EXPANDED = 280;
const DRAWER_WIDTH_COLLAPSED = 80;
const DRAWER_TRANSITION = 'width 200ms ease, margin 200ms ease';
const COLLAPSE_BTN_SIZE = 28;
const COLLAPSE_BTN_OVERHANG = 14; // half outside the rail

export interface HFAppShellProps {
  logo: ReactNode;
  logoCollapsed: ReactNode;
  navSlot?: ReactNode;
  footerNavSlot?: ReactNode;
  onLogout?: () => void;
  /** Controlled rail state. When set, the parent owns persistence. */
  navCollapsed?: boolean;
  onNavCollapsedChange?: (collapsed: boolean) => void;
  /** Max width of the page column. `false` lets a page bleed its own chrome across the whole main column. Defaults to `xl`. */
  pageMaxWidth?: HFPageContainerProps['maxWidth'];
  children: ReactNode;
}

function loadNavCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(NAV_COLLAPSED_KEY) === 'true';
}

export function HFAppShell({
  logo,
  logoCollapsed,
  navSlot,
  footerNavSlot,
  navCollapsed,
  onNavCollapsedChange,
  pageMaxWidth = 'xl',
  children,
}: HFAppShellProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isControlled = navCollapsed !== undefined;
  const [internalCollapsed, setCollapsed] = useState(loadNavCollapsed);
  const [tabletUnlocked, setTabletUnlocked] = useState(false);
  const collapsed = isTablet && !tabletUnlocked ? true : (navCollapsed ?? internalCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (isControlled) return;
    window.localStorage.setItem(NAV_COLLAPSED_KEY, String(internalCollapsed));
  }, [isControlled, internalCollapsed]);

  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const toggleCollapsed = useCallback(() => {
    const next = !collapsed;
    if (isTablet) setTabletUnlocked(true);
    if (!isControlled) setCollapsed(next);
    onNavCollapsedChange?.(next);
  }, [collapsed, isControlled, isTablet, onNavCollapsedChange]);

  useEffect(() => {
    if (!isTablet) setTabletUnlocked(false);
  }, [isTablet]);

  const drawerWidth = isMobile ? DRAWER_WIDTH_EXPANDED : collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;

  const showExpandedNav = isMobile || !collapsed;

  // Standard pattern: rail toggle on the drawer’s trailing edge (Ant Design / MUI dashboards).
  // Half hangs into main; root overflowX:hidden prevents document scroll.
  const edgeCollapseButton = !isMobile ? (
    <IconButton
      onClick={toggleCollapsed}
      size="small"
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      sx={{
        position: 'absolute',
        // Align with logo vertical center: header pt (32) + half logo row (24) − half button (14).
        top: 42,
        right: -COLLAPSE_BTN_OVERHANG,
        zIndex: theme.zIndex.drawer + 1,
        width: COLLAPSE_BTN_SIZE,
        height: COLLAPSE_BTN_SIZE,
        border: 1,
        borderColor: 'divider',
        borderRadius: '50%',
        bgcolor: 'background.paper',
        color: 'text.primary',
        boxShadow: 1,
        '&:hover': {
          bgcolor: 'background.paper',
        },
      }}
    >
      {collapsed ? <ChevronRightIcon sx={{ fontSize: 18 }} /> : <ChevronLeftIcon sx={{ fontSize: 18 }} />}
    </IconButton>
  ) : null;

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box
        sx={{
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
          // Container inset for the logo header (not sibling rhythm).
          // Extra top (32px) gives the mark breathing room; sides/bottom stay 24px.
          px: showExpandedNav ? 3 : 1,
          pt: 4,
          pb: 3,
        }}
      >
        {showExpandedNav ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minHeight: 48,
              pr: 1,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
              }}
            >
              {logo}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 48 }}>{logoCollapsed}</Box>
        )}
      </Box>

      {/* py is the nav region's container inset (not sibling rhythm) — matches the 24px drawer inset. */}
      <Box onClick={isMobile ? closeMobile : undefined} sx={{ flex: 1, overflow: 'auto', py: 3 }}>
        <List disablePadding sx={{ px: 2 }}>
          {navSlot}
        </List>
      </Box>

      {footerNavSlot ? (
        <Box
          onClick={isMobile ? closeMobile : undefined}
          sx={{
            flexShrink: 0,
            borderTop: 1,
            borderColor: 'divider',
            // Container inset for the footer rail (not sibling rhythm) — balanced 24px.
            py: 3,
          }}
        >
          <List disablePadding sx={{ px: 2 }}>
            {footerNavSlot}
          </List>
        </Box>
      ) : null}
    </Box>
  );

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh', overflowX: 'hidden' }}>
      <Box
        aria-hidden
        sx={{
          pointerEvents: 'none',
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          bgcolor: isDark ? hfAlpha(hf.navy800, 0.45) : hfAlpha(hf.bgDefault, 0.55),
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          minHeight: '100vh',
          bgcolor: 'transparent',
          overflowX: 'hidden',
        }}
      >
        {!isMobile ? (
          <>
            <Box aria-hidden sx={{ width: drawerWidth, flexShrink: 0, transition: DRAWER_TRANSITION }} />
            <Box
              data-testid="hf-app-nav-rail"
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: drawerWidth,
                height: '100dvh',
                maxHeight: '100dvh',
                zIndex: theme.zIndex.drawer,
                transition: DRAWER_TRANSITION,
              }}
            >
              {edgeCollapseButton}
              <Drawer
                variant="permanent"
                open
                sx={{
                  width: drawerWidth,
                  height: '100%',
                  transition: DRAWER_TRANSITION,
                  '& .MuiDrawer-paper': {
                    position: 'relative',
                    width: drawerWidth,
                    height: '100%',
                    maxHeight: '100dvh',
                    boxSizing: 'border-box',
                    transition: DRAWER_TRANSITION,
                    overflowX: 'hidden',
                    overflowY: 'hidden',
                    borderRight: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  },
                }}
              >
                <NavCollapsedContext.Provider value={collapsed}>{drawerContent}</NavCollapsedContext.Provider>
              </Drawer>
            </Box>
          </>
        ) : null}

        {isMobile ? (
          <Drawer
            id="hf-mobile-nav-drawer"
            variant="temporary"
            open={mobileOpen}
            onClose={closeMobile}
            ModalProps={{ keepMounted: true }}
            sx={{
              '& .MuiDrawer-paper': {
                width: DRAWER_WIDTH_EXPANDED,
                boxSizing: 'border-box',
                bgcolor: 'background.paper',
              },
            }}
          >
            <NavCollapsedContext.Provider value={false}>{drawerContent}</NavCollapsedContext.Provider>
          </Drawer>
        ) : null}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
            transition: DRAWER_TRANSITION,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            // Gap (not top margin on children) separates app bar / page content.
            gap: 3,
          }}
        >
          {isMobile ? (
            <AppBar
              position="sticky"
              elevation={0}
              sx={{
                bgcolor: 'background.paper',
                borderBottom: 1,
                borderColor: 'divider',
                color: 'text.primary',
              }}
            >
              <Toolbar sx={{ position: 'relative', minHeight: 64, px: 3 }}>
                <IconButton
                  edge="start"
                  onClick={() => setMobileOpen(true)}
                  aria-label="Open navigation"
                  aria-controls="hf-mobile-nav-drawer"
                  aria-expanded={mobileOpen}
                  sx={{ color: 'text.primary', zIndex: 1 }}
                >
                  <MenuIcon sx={{ fontSize: 28 }} />
                </IconButton>
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <Box sx={{ pointerEvents: 'auto', lineHeight: 0 }}>{logo}</Box>
                </Box>
              </Toolbar>
            </AppBar>
          ) : null}

          <HFPageContainer maxWidth={pageMaxWidth}>{children}</HFPageContainer>
        </Box>
      </Box>
    </Box>
  );
}
